import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Tooltip } from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';

// ─── Dark mode (localStorage tabanlı, ThemeContext opsiyonel) ─────────────────
const useDarkMode = () => {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('klc-theme') === 'dark'; } catch { return false; }
  });
  const toggle = () => {
    const next = !isDark;
    setIsDark(next);
    try { localStorage.setItem('klc-theme', next ? 'dark' : 'light'); } catch {}
    // MUI ThemeProvider kullanıyorsanız burada context toggle'ı çağırın
    window.dispatchEvent(new CustomEvent('klc-theme-change', { detail: next }));
  };
  return { isDark, toggle };
};

// ─── SVG İkonlar ─────────────────────────────────────────────────────────────
const SvgIcon: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color}
         strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const IconDashboard   = ({ c }: { c: string }) => <SvgIcon color={c}><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></SvgIcon>;
const IconTeklifler   = ({ c }: { c: string }) => <SvgIcon color={c}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></SvgIcon>;
const IconMusteriler  = ({ c }: { c: string }) => <SvgIcon color={c}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></SvgIcon>;
const IconUrunler     = ({ c }: { c: string }) => <SvgIcon color={c}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></SvgIcon>;
const IconFiyat       = ({ c }: { c: string }) => <SvgIcon color={c}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></SvgIcon>;
const IconKarsilastir = ({ c }: { c: string }) => <SvgIcon color={c}><line x1="12" y1="3" x2="12" y2="21"/><path d="M5 8l7-5 7 5"/><path d="M5 16l7 5 7-5"/></SvgIcon>;
const IconKullanici   = ({ c }: { c: string }) => <SvgIcon color={c}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><circle cx="19" cy="9" r="2"/><path d="M19 7v.5M19 11v.5M17.5 9H17M21 9h-.5"/></SvgIcon>;
const IconStats       = ({ c }: { c: string }) => <SvgIcon color={c}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></SvgIcon>;
const IconMaliyet     = ({ c }: { c: string }) => <SvgIcon color={c}><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></SvgIcon>;
const IconRakip       = ({ c }: { c: string }) => <SvgIcon color={c}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></SvgIcon>;
const IconHammadde    = ({ c }: { c: string }) => <SvgIcon color={c}><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></SvgIcon>;

// ─── Nav items ────────────────────────────────────────────────────────────────
interface NavItem { text: string; Icon: React.FC<{ c: string }>; path: string; color: string; }

const menuItems: NavItem[] = [
  { text: 'Dashboard',      Icon: IconDashboard,   path: '/dashboard',  color: '#7EB5D5' },
  { text: 'Teklifler',      Icon: IconTeklifler,   path: '/offers',     color: '#C49A62' },
  { text: 'Müşteriler',     Icon: IconMusteriler,  path: '/customers',  color: '#5FAD82' },
  { text: 'Ürünler',        Icon: IconUrunler,     path: '/products',   color: '#9B82C8' },
  { text: 'Fiyat Listesi',  Icon: IconFiyat,       path: '/price-list', color: '#5AACB2' },
  { text: 'Karşılaştırma',  Icon: IconKarsilastir, path: '/comparison', color: '#7C91C8' },
];

const adminItems: NavItem[] = [
  { text: 'Kullanıcılar',    Icon: IconKullanici, path: '/admin',                   color: '#C47DAE' },
  { text: 'İstatistikler',   Icon: IconStats,     path: '/admin/stats',             color: '#C48E60' },
  { text: 'Maliyet Params',  Icon: IconMaliyet,   path: '/admin/market-params',     color: '#5AADA9' },
  { text: 'Rakip Fiyatları', Icon: IconRakip,     path: '/admin/competitor-prices', color: '#B86F6F' },
  { text: 'Hammadde',        Icon: IconHammadde,  path: '/materials',               color: '#B4A55E' },
];

// ─── MenuItemRow ──────────────────────────────────────────────────────────────
const MenuItemRow: React.FC<{
  item: NavItem; active: boolean; collapsed: boolean; onClick: () => void;
}> = ({ item, active, collapsed, onClick }) => {
  const [hovered, setHovered] = useState(false);

  const row = (
      <div onClick={onClick}
           onMouseEnter={() => setHovered(true)}
           onMouseLeave={() => setHovered(false)}
           style={{
             display: 'flex', alignItems: 'center', gap: '10px',
             padding: collapsed ? '7px 0' : '7px 10px',
             margin: '1px 8px',
             justifyContent: collapsed ? 'center' : 'flex-start',
             cursor: 'pointer',
             background: active
                 ? `${item.color}18`
                 : hovered ? 'rgba(255,255,255,0.05)' : 'transparent',
             borderRadius: '9px',
             borderLeft: collapsed ? 'none' : active ? `3px solid ${item.color}` : '3px solid transparent',
             transition: 'all 0.15s ease',
             minHeight: '42px',
           }}
      >
        <div style={{
          width: '28px', height: '28px',
          background: active ? `${item.color}28` : hovered ? `${item.color}18` : `${item.color}12`,
          borderRadius: '7px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, transition: 'background 0.15s ease',
          border: active ? `1px solid ${item.color}44` : '1px solid transparent',
        }}>
          <item.Icon c={active ? item.color : `${item.color}99`} />
        </div>
        {!collapsed && (
            <span style={{
              fontSize: '13px', fontFamily: 'Barlow, Inter, sans-serif',
              fontWeight: active ? 600 : 400,
              color: active ? '#ffffff' : 'rgba(255,255,255,0.5)',
              whiteSpace: 'nowrap', letterSpacing: '-0.1px',
              transition: 'color 0.15s',
            }}>
          {item.text}
        </span>
        )}
      </div>
  );

  return collapsed
      ? <Tooltip title={item.text} placement="right" arrow>{row}</Tooltip>
      : row;
};

// ─── Layout ───────────────────────────────────────────────────────────────────
const DRAWER_WIDTH    = 248;
const COLLAPSED_WIDTH = 68;

const Layout: React.FC = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { isDark, toggle: toggleDark } = useDarkMode();

  const handleLogout = () => { logout(); navigate('/login'); };

  const isActive = (path: string) => {
    if (location.pathname === path) return true;
    if (path === '/dashboard' || path === '/admin') return location.pathname === path;
    if (path === '/materials') return location.pathname === '/materials' || location.pathname.startsWith('/materials?');
    return location.pathname.startsWith(path + '/');
  };

  const currentPageName =
      menuItems.find(m => isActive(m.path))?.text ||
      adminItems.find(m => isActive(m.path))?.text ||
      'KLC Kablo';

  const sidebarWidth = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;
  const initials = user?.fullName?.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  return (
      <div style={{ display: 'flex', minHeight: '100vh', background: isDark ? '#0d1117' : '#F1F5F9' }}>

        {/* ── Sidebar ── */}
        <div style={{
          width: `${sidebarWidth}px`,
          background: '#0A1628',
          display: 'flex', flexDirection: 'column',
          position: 'fixed', top: 0, left: 0, bottom: 0,
          zIndex: 100,
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          boxShadow: '2px 0 20px rgba(0,0,0,0.25)',
        }}>

          {/* Logo */}
          <div style={{
            padding: '0 14px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            display: 'flex', alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            minHeight: '68px', flexShrink: 0,
          }}>
            {collapsed ? (
                <Tooltip title="KLC Kablo" placement="right" arrow>
                  <div onClick={() => setCollapsed(false)} style={{
                    width: '36px', height: '36px',
                    background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'Barlow Condensed, sans-serif',
                    fontWeight: 900, color: 'white', fontSize: '18px',
                    cursor: 'pointer', borderRadius: '9px',
                    boxShadow: '0 2px 10px rgba(220,38,38,0.3)',
                  }}>K</div>
                </Tooltip>
            ) : (
                <img src="/klc-logo.png" alt="KLC Kablo"
                     style={{ height: '36px', objectFit: 'contain', cursor: 'pointer' }}
                     onClick={() => navigate('/dashboard')} />
            )}
            {!collapsed && (
                <button onClick={() => setCollapsed(true)} title="Küçült" style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.09)',
                  color: 'rgba(255,255,255,0.35)',
                  cursor: 'pointer', padding: '6px', borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s', flexShrink: 0,
                }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='rgba(255,255,255,0.1)'; (e.currentTarget as HTMLButtonElement).style.color='white'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='rgba(255,255,255,0.05)'; (e.currentTarget as HTMLButtonElement).style.color='rgba(255,255,255,0.35)'; }}>
                  <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
                </button>
            )}
          </div>

          {/* Nav */}
          <nav style={{ flex: 1, padding: '10px 0', overflowY: 'auto', overflowX: 'hidden' }}>
            {!collapsed && (
                <div style={{ padding: '4px 18px 6px', fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.18)', fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
                  Menü
                </div>
            )}
            {menuItems.map(item => (
                <MenuItemRow key={item.path} item={item} active={isActive(item.path)} collapsed={collapsed} onClick={() => navigate(item.path)} />
            ))}

            {isAdmin && (
                <>
                  <div style={{ margin: '10px 18px', borderTop: '1px solid rgba(255,255,255,0.07)' }} />
                  {!collapsed && (
                      <div style={{ padding: '0 18px 6px', fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.18)', fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
                        Yönetim
                      </div>
                  )}
                  {adminItems.map(item => (
                      <MenuItemRow key={item.path} item={item} active={isActive(item.path)} collapsed={collapsed} onClick={() => navigate(item.path)} />
                  ))}
                </>
            )}
          </nav>

          {/* Dark mode toggle */}
          <div style={{ padding: '8px 10px 4px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <Tooltip title={isDark ? 'Açık Mod' : 'Koyu Mod'} placement="right" arrow>
              <button onClick={toggleDark} style={{
                width: '100%', padding: collapsed ? '8px' : '8px 12px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '9px', cursor: 'pointer',
                display: 'flex', alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'space-between',
                gap: '8px', transition: 'all 0.15s',
                color: 'rgba(255,255,255,0.4)',
              }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='rgba(255,255,255,0.08)'; (e.currentTarget as HTMLButtonElement).style.color='rgba(255,255,255,0.7)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='rgba(255,255,255,0.04)'; (e.currentTarget as HTMLButtonElement).style.color='rgba(255,255,255,0.4)'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isDark
                      ? <LightModeRoundedIcon sx={{ fontSize: 15, color: '#FCD34D' }} />
                      : <DarkModeRoundedIcon  sx={{ fontSize: 15, color: 'rgba(255,255,255,0.5)' }} />
                  }
                  {!collapsed && (
                      <span style={{ fontSize: '12px', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.5px', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>
                    {isDark ? 'AÇIK MOD' : 'KOYU MOD'}
                  </span>
                  )}
                </div>
                {/* Toggle pill */}
                {!collapsed && (
                    <div style={{
                      width: '28px', height: '16px', borderRadius: '8px',
                      background: isDark ? '#DC2626' : 'rgba(255,255,255,0.15)',
                      position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                    }}>
                      <div style={{
                        position: 'absolute', top: 2, left: isDark ? 14 : 2,
                        width: 12, height: 12, borderRadius: '50%',
                        background: '#fff', transition: 'left 0.2s',
                      }} />
                    </div>
                )}
              </button>
            </Tooltip>
          </div>

          {/* Kullanıcı & Çıkış */}
          <div style={{ padding: '8px 10px 12px' }}>
            {/* Kullanıcı kartı */}
            {!collapsed && user && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 10px',
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: '10px', marginBottom: '8px',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <div style={{
                    width: '32px', height: '32px',
                    background: 'linear-gradient(135deg, #1E3A5F, #2D4F7C)',
                    borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: 700,
                    color: 'white', fontFamily: 'Barlow Condensed, sans-serif',
                    border: '1.5px solid rgba(255,255,255,0.1)',
                  }}>{initials}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.88)', fontFamily: 'Barlow, sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {user?.fullName}
                    </div>
                    <div style={{ fontSize: '10px', color: user?.role === 'ADMIN' ? '#F87171' : 'rgba(255,255,255,0.3)', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                      {user?.role === 'ADMIN' ? 'Admin' : 'Kullanıcı'}
                    </div>
                  </div>
                </div>
            )}
            {collapsed && user && (
                <Tooltip title={user.fullName || ''} placement="right" arrow>
                  <div style={{
                    width: '38px', height: '38px',
                    background: 'linear-gradient(135deg, #1E3A5F, #2D4F7C)',
                    borderRadius: '50%', margin: '0 auto 8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '13px', fontWeight: 700,
                    color: 'white', fontFamily: 'Barlow Condensed, sans-serif',
                    border: '1.5px solid rgba(255,255,255,0.1)', cursor: 'default',
                  }}>{initials}</div>
                </Tooltip>
            )}

            {/* Çıkış */}
            <Tooltip title={collapsed ? 'Çıkış' : ''} placement="right" arrow>
              <button onClick={handleLogout} style={{
                width: '100%', padding: collapsed ? '8px' : '8px 12px',
                background: 'rgba(220,38,38,0.09)',
                border: '1px solid rgba(220,38,38,0.2)',
                borderRadius: '8px', color: '#FCA5A5',
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: '11px', letterSpacing: '1.5px',
                cursor: 'pointer', transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
              }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background='rgba(220,38,38,0.18)'; (e.currentTarget as HTMLButtonElement).style.borderColor='rgba(220,38,38,0.38)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background='rgba(220,38,38,0.09)'; (e.currentTarget as HTMLButtonElement).style.borderColor='rgba(220,38,38,0.2)'; }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FCA5A5" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                {!collapsed && <span>ÇIKIŞ</span>}
              </button>
            </Tooltip>
          </div>
        </div>

        {/* ── Sağ taraf ── */}
        <div style={{
          marginLeft: `${sidebarWidth}px`, flex: 1, display: 'flex', flexDirection: 'column',
          transition: 'margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: '100vh',
        }}>
          {/* Topbar */}
          <div style={{
            height: '60px',
            background: isDark ? '#161b22' : '#ffffff',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
            display: 'flex', alignItems: 'center',
            padding: '0 28px', justifyContent: 'space-between',
            position: 'sticky', top: 0, zIndex: 50,
            boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.05)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {collapsed && (
                  <button onClick={() => setCollapsed(false)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '6px',
                    display: 'flex', alignItems: 'center', borderRadius: '8px', transition: 'background 0.15s',
                  }}
                          onMouseEnter={e => (e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : '#F1F5F9')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <MenuRoundedIcon sx={{ fontSize: 20, color: isDark ? '#8b949e' : '#64748B' }} />
                  </button>
              )}
              {/* Breadcrumb */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '12px', color: isDark ? '#8b949e' : '#94a3b8' }}>KLC Kablo</span>
                <span style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.15)' : '#d0d4de' }}>/</span>
                <span style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '17px', fontWeight: 700,
                  color: isDark ? '#e6edf3' : '#0A1628',
                  letterSpacing: '0.5px',
                }}>{currentPageName}</span>
              </div>
            </div>

            {/* Kullanıcı pill */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '5px 12px',
              background: isDark ? 'rgba(255,255,255,0.05)' : '#F8FAFC',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
              borderRadius: '10px',
            }}>
              <div style={{
                width: '28px', height: '28px',
                background: 'linear-gradient(135deg, #0A1628, #1E3A5F)',
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: 700,
                color: 'white', fontFamily: 'Barlow Condensed, sans-serif', flexShrink: 0,
              }}>{initials}</div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: isDark ? '#e6edf3' : '#0A1628', fontFamily: 'Barlow, sans-serif', lineHeight: 1.2 }}>
                  {user?.fullName}
                </div>
                <div style={{ fontSize: '10px', color: '#DC2626', fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                  {user?.role === 'ADMIN' ? 'Admin' : 'Kullanıcı'}
                </div>
              </div>
            </div>
          </div>

          {/* İçerik */}
          <div style={{ flex: 1, padding: '28px', background: isDark ? '#0d1117' : '#F1F5F9' }}>
            <Outlet />
          </div>
        </div>
      </div>
  );
};

export default Layout;
